import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { useFetchReviewerEarningsQuery } from 'app/apis/reviews';
import { AgreementsList } from 'app/agreements/components/agreement-list';
import { useAppToast } from 'app/common/hooks/use-app-toast';
import { useFetchQueueEarningsSummaryQuery } from 'app/pensieve/queries/enhanced';

import { EarningsCard } from './earnings-card';
import { getReadablePSTDate } from './stats-helpers';
import styles from './stats.module.scss';

export const Stats = () => {
  const { t } = useTranslation();
  const toast = useAppToast();
  const [startDate] = useState(() => dayjs().subtract(60, 'days'));
  const [endDate] = useState(() => dayjs());
  const [earningsStartDate] = useState(() => dayjs().utc().startOf('month').toISOString());

  const { data: earnings, isError: isEarningsError } = useFetchReviewerEarningsQuery(earningsStartDate);
  const { data: queueEarnings, isError: isQueueEarningsError } = useFetchQueueEarningsSummaryQuery({
    start: earningsStartDate,
  });
  const questionQueueEarnings = queueEarnings?.queue?.earnings;
  const hasEarningsError = isEarningsError || isQueueEarningsError;

  useEffect(() => {
    if (hasEarningsError) toast({ variant: 'error', description: t('error.couldNotLoadEarnings') });
  }, [hasEarningsError]);

  const totalEarnings =
    (earnings?.earnings ? parseFloat(earnings.earnings) : 0) +
    (questionQueueEarnings ? parseFloat(questionQueueEarnings) : 0);

  const exchangeCurrencyEarnings = earnings ? earnings.exchange_currency_earnings : null;

  const monthText = endDate.format('MMMM');

  return (
    <div className={styles['stats']}>
      <header className={styles['header']}>
        <h2>{t('queue.overview.yourEarnings')}</h2>
      </header>
      <main>
        <EarningsCard key="usd_earnings_card" earned={totalEarnings.toString()} monthText={monthText} />

        {exchangeCurrencyEarnings &&
          exchangeCurrencyEarnings.map((exchangeCurrencyEarning) => {
            const exchangeCurrency = exchangeCurrencyEarning.exchange_currency;
            const exchangeCurrencyTotal = exchangeCurrencyEarning.exchange_currency_total;

            if (exchangeCurrency && exchangeCurrencyTotal) {
              return (
                <EarningsCard
                  key={`${exchangeCurrency}_earnings_card`}
                  earned={exchangeCurrencyTotal}
                  monthText={monthText}
                  currency={exchangeCurrency}
                />
              );
            }

            return null;
          })}
      </main>

      <header className={styles['header']}>
        <h2> {t('queue.overview.yourRatings')} </h2>
      </header>
      <main>
        <div className={styles['stats-period']}>
          <p>
            <Trans
              i18nKey="queue.overview.period"
              values={{
                startDate: getReadablePSTDate(startDate),
                endDate: getReadablePSTDate(endDate),
              }}
              components={{
                start_date_time: <time dateTime={startDate.toString()} />,
                end_date_time: <time dateTime={endDate.toString()} />,
              }}
            />{' '}
          </p>
        </div>
        <p className={styles['ratings-placeholder']}>{t('queue.overview.wereRevisitingRatings')}</p>
      </main>

      <AgreementsList />
    </div>
  );
};

export default Stats;
